import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';

interface Participant {
  user_id: number; full_name: string; email: string; org_layer: string;
  department_id: number | null; department_name: string | null; manager_name: string | null;
  start_date: string | null; recent_starter: boolean; included: boolean; assignments: number; submitted: number;
}
interface Cycle { id: number; name: string; year: number; status: string; }

const layerLabel: Record<string, string> = { employee: 'עובד/ת', manager: 'מנהל/ת', hq: 'מטה', ceo: 'מנהלת הרשות', admin: 'מנהל מערכת' };
const statusLabel: Record<string, string> = { draft: 'טיוטה', active: 'פעיל', closed: 'סגור' };
const statusClass: Record<string, string> = { draft: 'badge-pending', active: 'badge-active', closed: 'badge-closed' };

/** Admin-only: who takes part in THIS cycle. Each cycle keeps its own list, so it can change from year to year. */
export default function AdminParticipants() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState<Cycle | null>(null);
  const [people, setPeople] = useState<Participant[]>([]);
  const [filter, setFilter] = useState('');
  const [dept, setDept] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = () => api.get(`/cycles/${cycleId}/participants`)
    .then(({ data }) => { setCycle(data.cycle); setPeople(data.participants); })
    .catch(() => navigate('/admin/cycles'));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cycleId]);

  const departments = useMemo(() => {
    const names = Array.from(new Set(people.map(p => p.department_name || 'ללא מחלקה')));
    return names.sort((a, b) => a.localeCompare(b, 'he'));
  }, [people]);

  const filtered = people.filter(p =>
    (!dept || (p.department_name || 'ללא מחלקה') === dept) &&
    (!filter || p.full_name.includes(filter) || p.email.includes(filter) || (p.manager_name || '').includes(filter))
  );
  const includedCount = people.filter(p => p.included).length;
  const locked = cycle?.status === 'closed';

  const setIncluded = async (ids: number[], included: boolean) => {
    if (!ids.length || locked) return;
    if (cycle?.status === 'active' && !included) {
      const n = people.filter(p => ids.includes(p.user_id) && p.assignments > 0).length;
      if (n > 0 && !window.confirm(`התהליך פעיל. הסרה תמחק את השאלונים שטרם הוגשו של ${n === 1 ? 'משתתף/ת אחד/ת' : n + ' משתתפים'} (שאלונים שכבר הוגשו נשמרים). להמשיך?`)) return;
    }
    setBusy(true); setError(''); setNotice('');
    try {
      const { data } = await api.put(`/cycles/${cycleId}/participants`, { user_ids: ids, included });
      await load();
      if (cycle?.status === 'active') {
        setNotice(included ? `${data.updated} משתתפים נוספו, השאלונים שלהם נוצרו ונשלחו ${data.emails?.setup ?? 0} קישורי סיסמה ו-${data.emails?.started ?? 0} הודעות.` : `${data.updated} הוסרו. ${data.removed_assignments} שאלונים שטרם הוגשו נמחקו.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'שגיאה בשמירה');
    } finally { setBusy(false); }
  };

  if (!cycle) return <div className="loading"><div className="spinner" />טוען משתתפים...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/cycles')}>← חזרה לתהליכים</button>
        <h1 style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          משתתפים: {cycle.name}
          <span className={`badge ${statusClass[cycle.status]}`}>{statusLabel[cycle.status]}</span>
        </h1>
        <div className="text-muted text-sm">{includedCount} מתוך {people.length} עובדים נכללים בתהליך זה</div>
      </div>

      <div className="alert alert-info" style={{ marginBottom: '16px' }}>
        {cycle.status === 'draft' && 'סמנו מי נכלל/ת בתהליך זה. עובדים שהתחילו לפני פחות מ-3 חודשים אינם מסומנים כברירת מחדל, וניתן לסמן אותם ידנית. בלחיצה על "הפעל תהליך" ייווצרו השאלונים למסומנים בלבד, וכל אחד/ת מהם יקבל/תקבל מייל (קישור להגדרת סיסמה למי שעדיין אין לו/ה, או הודעה שהתהליך יצא לדרך).'}
        {cycle.status === 'active' && 'התהליך פעיל: סימון עובד/ת יוצר מיד את השאלונים שלו/ה ושולח לו/ה מייל; ביטול סימון מוחק את השאלונים שטרם הוגשו (שאלונים שהוגשו נשמרים).'}
        {cycle.status === 'closed' && 'התהליך סגור – רשימת המשתתפים נעולה.'}
        {' '}הרשימה נפרדת לכל תהליך, כך שבכל שנה ניתן לבחור מחדש.
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
        <input className="form-input" style={{ width: '240px' }} placeholder="חיפוש לפי שם / מייל / מנהל" value={filter} onChange={e => setFilter(e.target.value)} />
        <select className="form-input" style={{ width: 'auto' }} value={dept} onChange={e => setDept(e.target.value)}>
          <option value="">כל המחלקות</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {!locked && (
          <>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setIncluded(filtered.filter(p => !p.included).map(p => p.user_id), true)}>✓ סמן את כל המוצגים</button>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setIncluded(filtered.filter(p => p.included).map(p => p.user_id), false)}>✗ בטל סימון למוצגים</button>
          </>
        )}
        <span className="text-muted text-sm">{filtered.length} מוצגים</span>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>נכלל/ת</th>
              <th>שם</th>
              <th>שכבה</th>
              <th>מחלקה</th>
              <th>מנהל/ת</th>
              <th>תאריך התחלה</th>
              {cycle.status !== 'draft' && <th>שאלונים</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.user_id} style={{ opacity: p.included ? 1 : 0.55 }}>
                <td>
                  <label className="checklist-item" style={{ margin: 0 }} title={locked ? 'התהליך סגור' : (p.included ? 'נכלל/ת בתהליך – לחץ/י כדי להסיר' : 'לא נכלל/ת – לחץ/י כדי לכלול')}>
                    <input type="checkbox" checked={p.included} disabled={busy || locked} onChange={e => setIncluded([p.user_id], e.target.checked)} />
                    <span className="text-sm">{p.included ? 'נכלל/ת' : 'לא נכלל/ת'}</span>
                  </label>
                </td>
                <td style={{ fontWeight: 600 }}>{p.full_name}<div className="text-muted" style={{ fontWeight: 400, fontSize: '12px', direction: 'ltr', textAlign: 'right' }}>{p.email}</div></td>
                <td>{layerLabel[p.org_layer] || p.org_layer}</td>
                <td>{p.department_name || '—'}</td>
                <td>{p.manager_name || '—'}</td>
                <td>
                  {p.start_date ? new Date(p.start_date).toLocaleDateString('he-IL') : '—'}
                  {p.recent_starter && <span className="badge badge-draft" style={{ marginRight: '6px' }}>פחות מ-3 חודשים</span>}
                </td>
                {cycle.status !== 'draft' && <td className="text-sm">{p.assignments ? `${p.submitted}/${p.assignments} הוגשו` : '—'}</td>}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-muted" style={{ textAlign: 'center' }}>אין עובדים להצגה</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
